import * as THREE from 'three'
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js'
import type { EnrichedPublicShop } from '../../../stores/shopStore'
import { getShopThumbnailCandidates } from '../../../utils/shopThumbnail'
import type { FloorLayout, MallLayout, StorefrontSlot } from './types'
import { CORRIDOR_HALF_WIDTH, ENTRY_ZONE } from './mallLayout'
import { MallFinishes, buildFloorShell } from './mallArchitecture'
import { StorefrontKit, buildStorefront } from './mallStorefront'
import { buildFixtures, type Obstacle } from './mallFixtures'
import { makePosterPlaceholderTexture } from './mallTextures'
import { bearingTo, horizontalHalfFov, isHeld, isOffered, offerScore } from './mallProximity'
import { AMBIENCE, DEFAULT_AMBIENCE, type AmbienceId } from './mallAmbience'

const EYE_HEIGHT = 1.65
const MOVE_SPEED = 4.6 // world units / second — an unhurried browsing walk
const PITCH_LIMIT = Math.PI / 2 - 0.08
const WALL_BUFFER = 0.9

const TEXTURE_CONCURRENCY = 4

function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v))
}

export interface MallSceneOptions {
  onProximityChange: (shop: EnrichedPublicShop | null) => void
  /** Lighting mood to open with; switchable later via setAmbience(). */
  ambience?: AmbienceId
}

/** A material and the color it was authored with, before any ambience tint. */
interface TintTarget {
  material: THREE.Material & { color?: THREE.Color; emissive?: THREE.Color }
  baseColor: THREE.Color | null
  baseEmissive: THREE.Color | null
}

interface BuiltFloor {
  layout: FloorLayout
  group: THREE.Group
  kit: StorefrontKit
  obstacles: Obstacle[]
  disposeFixtures: () => void
}

/**
 * Imperative three.js scene manager for the 3D mall. Owns the renderer/scene/
 * camera lifecycle and the render loop. Movement/look state is pushed in by
 * an input controller (keyboard+mouse on desktop, touch on mobile) via
 * setMoveInput()/applyLookDelta() — this class has no DOM input listeners of
 * its own, keeping rendering and input concerns separate and independently
 * testable.
 *
 * Geometry construction lives in mallArchitecture / mallStorefront /
 * mallFixtures; this file is the assembly, the lighting rig and the loop.
 */
export class MallSceneManager {
  private renderer: THREE.WebGLRenderer
  private scene: THREE.Scene
  private camera: THREE.PerspectiveCamera
  private container: HTMLDivElement
  private layout: MallLayout

  private opts: MallSceneOptions

  private yaw = 0
  private pitch = 0
  private moveInput = { forward: 0, strafe: 0 }
  private bobPhase = 0
  private reducedMotion = false

  private currentFloorId: string | null = null
  private activePromptShopId: string | null = null

  private finishes = new MallFinishes()
  private hemisphere!: THREE.HemisphereLight
  private sun!: THREE.DirectionalLight
  private ambient!: THREE.AmbientLight
  private tintTargets: TintTarget[] = []
  private ambience: AmbienceId = DEFAULT_AMBIENCE
  private floors = new Map<string, BuiltFloor>()
  private envRT: THREE.WebGLRenderTarget | null = null

  private placeholders = new Map<string, THREE.CanvasTexture>()
  private textureQueue: Array<() => void> = []
  private inFlightTextures = 0
  private requestedFloorTextures = new Set<string>()
  private loadedPosters: THREE.Texture[] = []

  private posterMaterials = new Map<string, THREE.MeshBasicMaterial>() // key: shop.id

  /** Cached horizontal half-frustum; only changes when the viewport does. */
  private halfFov = 0

  private clock = new THREE.Clock()
  private animId = 0
  private disposed = false

  constructor(container: HTMLDivElement, layout: MallLayout, opts: MallSceneOptions) {
    this.container = container
    this.layout = layout
    this.opts = opts
    this.ambience = opts.ambience ?? DEFAULT_AMBIENCE
    this.reducedMotion = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches

    this.renderer = new THREE.WebGLRenderer({ antialias: true })
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    this.renderer.setSize(container.clientWidth, container.clientHeight)
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping
    this.renderer.outputColorSpace = THREE.SRGBColorSpace
    container.appendChild(this.renderer.domElement)

    this.scene = new THREE.Scene()
    // Interior haze, not a void. Distance reads as depth, the way it does when
    // you look down a real concourse toward a glazed end wall. Both the color
    // and the exposure are set by the ambience preset applied below.
    this.scene.background = new THREE.Color()
    this.scene.fog = new THREE.FogExp2(0x000000, 0.0075)

    this.camera = new THREE.PerspectiveCamera(
      66,
      Math.max(1, container.clientWidth) / Math.max(1, container.clientHeight),
      0.1,
      400
    )
    this.camera.rotation.order = 'YXZ'
    this.camera.position.set(0, EYE_HEIGHT, 0)
    this.halfFov = horizontalHalfFov(this.camera.fov, this.camera.aspect)

    this.setupLighting()

    layout.floors.forEach((floor) => this.buildFloor(floor))
    this.disableFogOnAdditives()
    this.collectTintTargets()
    this.applyAmbience(this.ambience)

    const first = layout.floors[0]
    if (first) this.teleportToFloor(first.id)

    this.animate = this.animate.bind(this)
    this.animId = requestAnimationFrame(this.animate)
  }

  // ── Lighting ────────────────────────────────────────────────────────

  /**
   * Three real lights, total, however many floors are loaded.
   *
   * Real concourses run at roughly 300–750 lux ambient with accent lighting
   * layered on merchandise at around 5:1, which in a naive translation would
   * mean dozens of point lights. Instead the environment map supplies the soft
   * bounce that makes stone and glass read correctly, a hemisphere light sets
   * the ambient level, one directional light stands in for the sun through the
   * vault, and every fitting, cove and downlight pool in the scene is emissive
   * geometry. Constant shader cost, and it looks lit rather than tinted.
   */
  private setupLighting() {
    const pmrem = new THREE.PMREMGenerator(this.renderer)
    this.envRT = pmrem.fromScene(new RoomEnvironment(), 0.04)
    this.scene.environment = this.envRT.texture
    pmrem.dispose()

    // Sky/ground bounce, the sun angled down the vault, and a weak fill so the
    // shopfront recesses don't bottom out. Colors and intensities are set by
    // applyAmbience(); these are placeholders. Restrained on purpose — the
    // environment map already contributes a full room's worth of bounce, and
    // doubling up clips everything to white.
    this.hemisphere = new THREE.HemisphereLight()
    this.scene.add(this.hemisphere)

    this.sun = new THREE.DirectionalLight()
    this.sun.position.set(6, 22, 8)
    this.scene.add(this.sun)

    this.ambient = new THREE.AmbientLight()
    this.scene.add(this.ambient)
  }

  // ── Ambience ────────────────────────────────────────────────────────

  /**
   * Records every material's authored color once, so a preset can be applied
   * (and re-applied, and undone) by multiplying from that baseline rather than
   * compounding tints on each switch.
   */
  private collectTintTargets() {
    const seen = new Set<THREE.Material>()
    this.scene.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) return
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material]
      for (const m of mats) {
        if (seen.has(m)) continue
        seen.add(m)
        const mat = m as TintTarget['material']
        this.tintTargets.push({
          material: mat,
          baseColor: mat.color ? mat.color.clone() : null,
          baseEmissive: mat.emissive ? mat.emissive.clone() : null,
        })
      }
    })
  }

  setAmbience(id: AmbienceId) {
    if (id === this.ambience) return
    this.applyAmbience(id)
  }

  getAmbience(): AmbienceId {
    return this.ambience
  }

  private applyAmbience(id: AmbienceId) {
    const preset = AMBIENCE[id]
    this.ambience = id

    const tint = new THREE.Color(preset.tint)
    for (const t of this.tintTargets) {
      if (t.baseColor) t.material.color!.copy(t.baseColor).multiply(tint)
      if (t.baseEmissive) t.material.emissive!.copy(t.baseEmissive).multiply(tint)
    }

    this.renderer.toneMappingExposure = preset.exposure
    this.scene.environmentIntensity = preset.environmentIntensity

    this.hemisphere.color.setHex(preset.skyLight)
    this.hemisphere.groundColor.setHex(preset.groundLight)
    this.hemisphere.intensity = preset.hemisphereIntensity

    this.sun.color.setHex(preset.sunColor)
    this.sun.intensity = preset.sunIntensity

    this.ambient.color.setHex(preset.ambientColor)
    this.ambient.intensity = preset.ambientIntensity

    ;(this.scene.background as THREE.Color).setHex(preset.atmosphere)
    const fog = this.scene.fog as THREE.FogExp2
    fog.color.setHex(preset.atmosphere)
    fog.density = preset.fogDensity
  }

  /**
   * Additive glows are stand-ins for light, not surfaces, so fogging them
   * brightens the distance instead of hazing it. Cheaper to fix here in one
   * sweep than to thread a flag through every builder.
   */
  private disableFogOnAdditives() {
    this.scene.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) return
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material]
      mats.forEach((m) => {
        if ((m as THREE.Material & { blending?: THREE.Blending }).blending === THREE.AdditiveBlending) {
          m.fog = false
        }
      })
    })
  }

  // ── Input surface (called by useMallInputController) ──────────────────

  setMoveInput(forward: number, strafe: number) {
    this.moveInput.forward = clamp(forward, -1, 1)
    this.moveInput.strafe = clamp(strafe, -1, 1)
  }

  /** dx/dy are already-scaled radians (pointer/touch delta * sensitivity). */
  applyLookDelta(dYaw: number, dPitch: number) {
    this.yaw -= dYaw
    this.pitch = clamp(this.pitch - dPitch, -PITCH_LIMIT, PITCH_LIMIT)
  }

  // ── Floor navigation ────────────────────────────────────────────────

  teleportToFloor(floorId: string) {
    const built = this.floors.get(floorId)
    if (!built) return
    const { entry } = built.layout

    // Only the wing you're in is drawn; the rest cost nothing.
    this.floors.forEach((f, id) => { f.group.visible = id === floorId })

    this.camera.position.set(entry.x, EYE_HEIGHT, entry.z)
    this.yaw = entry.yaw
    this.pitch = 0
    this.moveInput.forward = 0
    this.moveInput.strafe = 0
    this.bobPhase = 0
    this.currentFloorId = floorId
    this.activePromptShopId = null
    this.opts.onProximityChange(null)
    this.loadTexturesForFloor(floorId)
  }

  getCurrentFloorId(): string | null {
    return this.currentFloorId
  }

  // ── Viewport ────────────────────────────────────────────────────────

  resize(width: number, height: number) {
    if (width <= 0 || height <= 0) return
    this.camera.aspect = width / height
    this.camera.updateProjectionMatrix()
    // Whether a shop counts as "in view" is measured against the frustum, so
    // this has to follow the viewport.
    this.halfFov = horizontalHalfFov(this.camera.fov, this.camera.aspect)
    this.renderer.setSize(width, height)
  }

  // ── Scene construction ──────────────────────────────────────────────

  private buildFloor(floor: FloorLayout) {
    const group = new THREE.Group()
    group.add(buildFloorShell(floor, this.finishes))

    const kit = new StorefrontKit(floor, this.finishes)
    const placeholder = this.getPlaceholder(floor.color)
    floor.slots.forEach((slot) => {
      const { group: bay, posterMat } = buildStorefront(slot, kit, placeholder)
      this.posterMaterials.set(slot.shop.id, posterMat)
      group.add(bay)
    })

    const fixtures = buildFixtures(floor, this.finishes)
    group.add(fixtures.group)

    group.visible = false
    this.scene.add(group)
    this.floors.set(floor.id, {
      layout: floor,
      group,
      kit,
      obstacles: fixtures.obstacles,
      disposeFixtures: fixtures.dispose,
    })
  }

  private getPlaceholder(color: string): THREE.CanvasTexture {
    const cached = this.placeholders.get(color)
    if (cached) return cached
    const tex = makePosterPlaceholderTexture(color)
    this.placeholders.set(color, tex)
    return tex
  }

  // ── Texture loading (poster → thumbnail.jpg → frame_0000.jpg → placeholder) ──

  private loadTexturesForFloor(floorId: string) {
    if (this.requestedFloorTextures.has(floorId)) return
    this.requestedFloorTextures.add(floorId)
    const floor = this.layout.floors.find((f) => f.id === floorId)
    if (!floor) return
    const placeholder = this.getPlaceholder(floor.color)
    floor.slots.forEach((slot) => {
      this.loadShopTexture(slot.shop, placeholder, (tex) => {
        const mat = this.posterMaterials.get(slot.shop.id)
        if (mat && !this.disposed) {
          mat.map = tex
          mat.needsUpdate = true
        }
      })
    })
  }

  private loadShopTexture(shop: EnrichedPublicShop, placeholder: THREE.CanvasTexture, onReady: (tex: THREE.Texture) => void) {
    const candidates = getShopThumbnailCandidates(shop)
    let idx = 0

    const tryNext = () => {
      if (idx >= candidates.length) {
        onReady(placeholder)
        return
      }
      const url = candidates[idx++]
      this.queueTextureLoad(
        url,
        (tex) => {
          tex.colorSpace = THREE.SRGBColorSpace
          this.loadedPosters.push(tex)
          onReady(tex)
        },
        tryNext
      )
    }

    tryNext()
  }

  /**
   * Loads one URL the same way the browse page's shop cards do: a plain
   * <img>. TextureLoader's own image pipeline was rejecting posters the
   * cards render fine, so the two views now share one loading path — the
   * candidate list from getShopThumbnailCandidates, stepped through on
   * error, exactly like handleThumbnailImgError does for the cards.
   *
   * crossOrigin stays 'anonymous' because WebGL refuses to upload a tainted
   * image; the blob account and the /poster endpoint both answer with CORS
   * headers, so this costs nothing.
   */
  private queueTextureLoad(url: string, onLoad: (tex: THREE.Texture) => void, onError: () => void) {
    this.textureQueue.push(() => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.decoding = 'async'

      const settle = (fn: () => void) => {
        img.onload = null
        img.onerror = null
        this.inFlightTextures--
        this.pumpTextureQueue()
        fn()
      }

      img.onload = () => settle(() => {
        if (this.disposed) return
        const tex = new THREE.Texture(img)
        tex.needsUpdate = true
        onLoad(tex)
      })
      img.onerror = () => settle(onError)

      img.src = url
    })
    this.pumpTextureQueue()
  }

  private pumpTextureQueue() {
    while (this.inFlightTextures < TEXTURE_CONCURRENCY && this.textureQueue.length > 0) {
      const task = this.textureQueue.shift()!
      this.inFlightTextures++
      task()
    }
  }

  // ── Movement + collision ────────────────────────────────────────────

  /** Keeps you inside the concourse and out of the planters. */
  private constrain(floor: BuiltFloor) {
    const { originX, corridorLength } = floor.layout
    const p = this.camera.position

    const halfSpan = CORRIDOR_HALF_WIDTH - WALL_BUFFER
    p.x = clamp(p.x, originX - halfSpan, originX + halfSpan)
    p.z = clamp(p.z, -corridorLength + WALL_BUFFER, ENTRY_ZONE - WALL_BUFFER)

    for (const o of floor.obstacles) {
      const dx = p.x - o.x
      const dz = p.z - o.z
      const distSq = dx * dx + dz * dz
      if (distSq >= o.radius * o.radius) continue
      const dist = Math.sqrt(distSq)
      if (dist < 1e-4) {
        p.z = o.z + o.radius // degenerate: push straight down the concourse
        continue
      }
      p.x = o.x + (dx / dist) * o.radius
      p.z = o.z + (dz / dist) * o.radius
    }
  }

  // ── Proximity ───────────────────────────────────────────────────────

  private checkProximity(floor: BuiltFloor) {
    const view = {
      x: this.camera.position.x,
      z: this.camera.position.z,
      yaw: this.yaw,
      halfFov: this.halfFov,
    }

    let candidate: StorefrontSlot | null = null
    let bestScore = Infinity
    let activeHolds = false

    for (const slot of floor.layout.slots) {
      const bearing = bearingTo(view, slot.position.x, slot.position.z)

      if (slot.shop.id === this.activePromptShopId) activeHolds = isHeld(bearing)
      if (!isOffered(bearing)) continue

      const score = offerScore(bearing)
      if (score < bestScore) {
        bestScore = score
        candidate = slot
      }
    }

    // Once a shop is up it stays up on the looser thresholds, so small drifts
    // and head movements don't dismiss it.
    if (this.activePromptShopId && activeHolds) return

    const nextId = candidate?.shop.id ?? null
    if (nextId !== this.activePromptShopId) {
      this.activePromptShopId = nextId
      this.opts.onProximityChange(candidate?.shop ?? null)
    }
  }

  // ── Render loop ─────────────────────────────────────────────────────

  private animate() {
    if (this.disposed) return
    this.animId = requestAnimationFrame(this.animate)
    const dt = Math.min(this.clock.getDelta(), 0.1)

    this.camera.rotation.y = this.yaw
    this.camera.rotation.x = this.pitch

    const { forward, strafe } = this.moveInput
    const moving = forward !== 0 || strafe !== 0
    if (moving) {
      const sinY = Math.sin(this.yaw)
      const cosY = Math.cos(this.yaw)
      const speed = MOVE_SPEED * dt
      this.camera.position.x += (-sinY * forward + cosY * strafe) * speed
      this.camera.position.z += (-cosY * forward - sinY * strafe) * speed
      this.bobPhase += dt * 8.4 * Math.min(1, Math.hypot(forward, strafe))
    }

    // Barely-there walking bob. Enough to feel like footsteps, small enough that
    // you only notice it when it's missing.
    const bob = moving && !this.reducedMotion ? Math.sin(this.bobPhase) * 0.022 : 0
    this.camera.position.y = EYE_HEIGHT + bob

    const floor = this.currentFloorId ? this.floors.get(this.currentFloorId) : undefined
    if (floor) {
      this.constrain(floor)
      this.checkProximity(floor)
    }

    this.renderer.render(this.scene, this.camera)
  }

  // ── Cleanup ─────────────────────────────────────────────────────────

  dispose() {
    this.disposed = true
    cancelAnimationFrame(this.animId)

    this.floors.forEach((f) => {
      f.kit.dispose()
      f.disposeFixtures()
    })
    this.floors.clear()

    // Geometry built inline by the shell/storefront builders isn't owned by a
    // kit, so sweep the graph for anything still holding GPU memory. Materials
    // and textures already disposed above are safely idempotent.
    const seenGeo = new Set<THREE.BufferGeometry>()
    const seenMat = new Set<THREE.Material>()
    this.scene.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) return
      if (!seenGeo.has(obj.geometry)) {
        seenGeo.add(obj.geometry)
        obj.geometry.dispose()
      }
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material]
      mats.forEach((m) => {
        if (seenMat.has(m)) return
        seenMat.add(m)
        m.dispose()
      })
    })

    this.finishes.dispose()
    this.placeholders.forEach((t) => t.dispose())
    this.placeholders.clear()
    this.loadedPosters.forEach((t) => t.dispose())
    this.loadedPosters.length = 0
    this.posterMaterials.clear()
    this.envRT?.dispose()
    this.scene.environment = null

    this.renderer.dispose()
    if (this.renderer.domElement.parentElement === this.container) {
      this.container.removeChild(this.renderer.domElement)
    }
  }
}
